'use client';

import { useState, useMemo, useEffect } from 'react';
import { 
  MessageSquare, 
  Sparkles, 
  FolderOpen, 
  Compass, 
  BookOpen, 
  Palette, 
  Package, 
  PlusCircle,
  ChevronRight,
  ChevronDown,
  Search,
  Target,
  BarChart3,
  Users,
  Share2,
  Film,
  Map,
  Layers,
  LayoutGrid,
  Server,
  FileText,
  FileSignature,
  Code,
  FileCheck,
  Rocket,
  Network,
  CheckCircle,
  History,
  X,
} from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { useDiscovery } from '@/hooks/use-discovery';
import { useDefinition } from '@/hooks/use-definition';
import { useDesignArtifactsList, extractDesignMarkdown } from '@/hooks/use-design';
import { useDeliveryArtifactsList, extractDeliveryMarkdown } from '@/hooks/use-delivery';
import { isRouteDisabled } from '@/lib/feature-flags';
import { useNavigationGating } from '@/lib/navigation-gating';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SubItem {
  id: string;
  label: string;
  icon?: React.ElementType;
  countKey?: string;
  /** For Design/Delivery: artifact type to check for document marker */
  artifactType?: 'design-architecture' | 'design-areas' | 'delivery-architecture' | 'delivery-storymap' | 'delivery-mvp';
}

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  expandable?: boolean;
  subItems?: SubItem[];
  countKey?: string;
}

interface ArtifactCounts {
  goals?: number;
  personas?: number;
  interviews?: number;
  competitors?: number;
  journeys?: number;
  useCases?: number;
  documents?: number;
  stories?: number;
  serviceMaps?: number;
  storyboards?: number;
}

const sidebarItems: SidebarItem[] = [
  { id: 'chats', label: 'Chats', icon: MessageSquare },
  { id: 'guru', label: 'Product Guru', icon: Sparkles },
  { id: 'knowledge', label: 'Knowledge', icon: FolderOpen, countKey: 'documents' },
  { id: 'overview', label: 'Project Overview', icon: Compass },
  { 
    id: 'discovery', 
    label: 'Discovery', 
    icon: Search, 
    expandable: true,
    subItems: [
      { id: 'discovery-goals', label: 'Goals and Objectives', icon: Target, countKey: 'goals' },
      { id: 'discovery-interviews', label: 'Stakeholder Interviews', icon: MessageSquare, countKey: 'interviews' },
      { id: 'discovery-competitors', label: 'Competitor Analysis', icon: BarChart3, countKey: 'competitors' },
    ]
  },
  { 
    id: 'definition', 
    label: 'Definition', 
    icon: BookOpen, 
    expandable: true,
    subItems: [
      { id: 'definition-personas', label: 'Personas', icon: Users, countKey: 'personas' },
      { id: 'definition-usecases', label: 'Use Cases', icon: Target, countKey: 'useCases' },
      { id: 'definition-servicemap', label: 'Service Map', icon: Map, countKey: 'serviceMaps' },
      { id: 'definition-journeys', label: 'User Journeys', icon: Share2, countKey: 'journeys' },
      { id: 'definition-storyboards', label: 'Storyboard', icon: Film, countKey: 'storyboards' },
    ]
  },
  { 
    id: 'design', 
    label: 'Design', 
    icon: Palette, 
    expandable: true,
    subItems: [
      { id: 'design-architecture', label: 'Product Architecture', icon: Layers, artifactType: 'design-architecture' },
      { id: 'design-areas', label: 'Application Areas', icon: LayoutGrid, artifactType: 'design-areas' },
    ]
  },
  { 
    id: 'delivery', 
    label: 'Delivery', 
    icon: Package, 
    expandable: true,
    subItems: [
      { id: 'delivery-architecture', label: 'Technical Architecture', icon: Server, artifactType: 'delivery-architecture' },
      { id: 'delivery-storymap', label: 'Story Map', icon: Map, artifactType: 'delivery-storymap' },
      { id: 'delivery-mvp', label: 'MVP Scope', icon: Target, artifactType: 'delivery-mvp' },
      { id: 'delivery-stories', label: 'Stories', icon: BookOpen, countKey: 'stories' },
    ]
  },
  { id: 'base-specs', label: 'Base Specs', icon: FileText },
  { id: 'contracts', label: 'Contracts', icon: FileSignature },
  { id: 'technical-specs', label: 'Technical Specs', icon: Code },
  { id: 'evidence', label: 'Evidence', icon: FileCheck },
  { id: 'releases', label: 'Releases', icon: Rocket },
  { id: 'traceability', label: 'Traceability', icon: Network },
  { id: 'approvals', label: 'Approvals', icon: CheckCircle, badge: 0 }, // Badge count will be dynamic
  { id: 'audit-log', label: 'Audit Log', icon: History },
];

interface ProjectSidebarProps {
  projectId?: string;
  activeItem?: string;
  onItemClick?: (id: string) => void;
  onAddTemplate?: () => void;
  artifactCounts?: ArtifactCounts;
  isOpen?: boolean;
  onClose?: () => void;
}

export function ProjectSidebar({ 
  projectId,
  activeItem = 'guru', 
  onItemClick, 
  onAddTemplate,
  artifactCounts = {},
  isOpen = false,
  onClose,
}: ProjectSidebarProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Auto-expand the section containing the active item (e.g. on reload with ?tab=delivery-mvp)
  useEffect(() => {
    if (!activeItem) return;
    const parent = sidebarItems.find((item) => item.expandable && item.subItems?.some((s) => s.id === activeItem));
    if (parent) {
      setExpandedSections((prev) => new Set(prev).add(parent.id));
    }
  }, [activeItem]);

  // Use centralized navigation gating
  const { isItemDisabled, getBlockedTooltip } = useNavigationGating(projectId);

  // Fetch discovery counts directly
  const { data: discoveryGoals } = useDiscovery(projectId || '', 'goals');
  const { data: discoveryInterviews } = useDiscovery(projectId || '', 'interviews');
  const { data: discoveryCompetitors } = useDiscovery(projectId || '', 'competitors');

  const { data: designArtifacts = [] } = useDesignArtifactsList(projectId);
  const { data: deliveryArtifacts = [] } = useDeliveryArtifactsList(projectId);

  const hasArtifactDocument = (artifactType: SubItem['artifactType']): boolean => {
    if (!artifactType) return false;
    const hasSubstantialContent = (md: string | null) => (md?.trim().length ?? 0) > 100;
    if (artifactType.startsWith('design-')) {
      const type = artifactType.replace('design-', '') as 'architecture' | 'areas';
      const a = designArtifacts.find((x: { type: string }) => x.type === type);
      return !!a && hasSubstantialContent(extractDesignMarkdown(a.content));
    }
    if (artifactType.startsWith('delivery-')) {
      const type = artifactType.replace('delivery-', '') as 'architecture' | 'storymap' | 'mvp';
      const a = deliveryArtifacts.find((x: { type: string }) => x.type === type);
      return !!a && hasSubstantialContent(extractDeliveryMarkdown(a.content));
    }
    return false;
  };

  // Fetch definition counts directly using the correct hook
  const { data: defPersonas } = useDefinition(projectId || '', 'personas');
  const { data: defUseCases } = useDefinition(projectId || '', 'use-cases');
  const { data: defJourneys } = useDefinition(projectId || '', 'journeys');
  const { data: defServiceMaps } = useDefinition(projectId || '', 'service-maps');
  const { data: defStoryboards } = useDefinition(projectId || '', 'storyboards');

  // Merge all counts
  const mergedCounts = useMemo(() => ({
    ...artifactCounts,
    // Discovery counts
    goals: discoveryGoals?.items?.length ?? artifactCounts.goals,
    interviews: discoveryInterviews?.items?.length ?? artifactCounts.interviews,
    competitors: discoveryCompetitors?.items?.length ?? artifactCounts.competitors,
    // Definition counts
    personas: defPersonas?.items?.length ?? artifactCounts.personas,
    useCases: defUseCases?.items?.length ?? artifactCounts.useCases,
    journeys: defJourneys?.items?.length ?? artifactCounts.journeys,
    serviceMaps: defServiceMaps?.items?.length ?? artifactCounts.serviceMaps,
    storyboards: defStoryboards?.items?.length ?? artifactCounts.storyboards,
  }), [artifactCounts, discoveryGoals, discoveryInterviews, discoveryCompetitors, defPersonas, defUseCases, defJourneys, defServiceMaps, defStoryboards]);

  const toggleExpanded = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleItemClick = (item: SidebarItem) => {
    if (item.expandable && item.subItems) {
      toggleExpanded(item.id);
    } else {
      handleMobileItemClick(item.id);
    }
  };

  const isActive = (id: string) => {
    return activeItem === id || activeItem.startsWith(id + '-');
  };

  const isSubItemActive = (subItemId: string) => {
    return activeItem === subItemId;
  };

  const getCount = (countKey?: string): number | undefined => {
    if (!countKey) return undefined;
    return mergedCounts[countKey as keyof ArtifactCounts];
  };

  // Calculate total count for expandable sections
  const getSectionCount = (item: SidebarItem): number => {
    if (!item.subItems) return 0;
    return item.subItems.reduce((total, subItem) => {
      const count = getCount(subItem.countKey);
      return total + (count || 0);
    }, 0);
  };

  // Render a sidebar item button (reusable for normal and disabled states)
  const renderSidebarButton = (
    item: SidebarItem,
    isExpanded: boolean,
    itemIsActive: boolean,
    itemCount: number | undefined,
    disabled: boolean
  ) => {
    const Icon = item.icon;
    
    return (
      <button
        onClick={() => !disabled && handleItemClick(item)}
        disabled={disabled}
        className={cn(
          'w-full h-10 flex items-center justify-between px-3 rounded-[10px] text-base transition-colors',
          disabled
            ? 'text-gray-400 cursor-not-allowed'
            : itemIsActive && !item.expandable
              ? 'bg-brand-100 text-brand-800'
              : 'text-gray-700 hover:bg-surface-secondary'
        )}
      >
        <div className="flex items-center gap-2">
          <Icon className={cn('w-5 h-5', disabled && 'opacity-50')} />
          <span className="tracking-tight">{item.label}</span>
          {item.badge && !disabled && (
            <span className="px-2 py-0.5 bg-brand-700 text-white text-xs rounded-full min-w-[23px] text-center">
              {item.badge}
            </span>
          )}
          {itemCount !== undefined && itemCount > 0 && !item.badge && !disabled && (
            <span className="px-2 py-0.5 bg-surface-tertiary text-gray-600 text-xs rounded-full min-w-[23px] text-center">
              {itemCount}
            </span>
          )}
        </div>
        {item.expandable && (
          isExpanded ? (
            <ChevronDown className={cn('w-4 h-4', disabled ? 'text-gray-300' : 'text-gray-400')} />
          ) : (
            <ChevronRight className={cn('w-4 h-4', disabled ? 'text-gray-300' : 'text-gray-400')} />
          )
        )}
      </button>
    );
  };

  // Handle item click and close sidebar on mobile
  const handleMobileItemClick = (id: string) => {
    onItemClick?.(id);
    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) {
      onClose?.();
    }
  };

  return (
    <TooltipProvider>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={cn(
        "bg-white border-r border-surface-border h-full flex flex-col",
        // Mobile: fixed overlay, slide in from left
        "fixed md:relative inset-y-0 left-0 z-50 md:z-auto",
        "w-[280px] md:w-[320px]",
        "transform transition-transform duration-300 ease-in-out md:transform-none",
        isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}>
        {/* Mobile close button */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-surface-border">
          <span className="font-medium text-gray-900">Navigation</span>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-secondary"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <nav className="flex-1 p-4 flex flex-col gap-1 overflow-y-auto">
          {sidebarItems.map((item) => {
            const isExpanded = expandedSections.has(item.id);
            const itemIsActive = isActive(item.id);
            const itemCount = item.countKey ? getCount(item.countKey) : (item.expandable ? getSectionCount(item) : undefined);
            
            // Check if this item should be disabled
            // Priority: feature flag (hidden/coming soon) > workflow gating
            const isHiddenByFeatureFlag = isRouteDisabled(item.id);
            const isBlockedByGating = isItemDisabled(item.id);
            const shouldBeDisabled = isHiddenByFeatureFlag || isBlockedByGating;
            
            // Skip rendering items that are hidden by feature flags (enterprise items)
            if (isHiddenByFeatureFlag) {
              return null;
            }
            
            // Determine tooltip message for gated items
            const getTooltipMessage = () => {
              if (isBlockedByGating) {
                return getBlockedTooltip(item.id);
              }
              return '';
            };
            
            return (
              <div key={item.id}>
                {shouldBeDisabled ? (
                  // Wrap disabled button in a span for tooltip hover (disabled buttons don't receive hover events)
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block w-full">
                        {renderSidebarButton(item, isExpanded, itemIsActive, itemCount, true)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[280px]">
                      <p>{getTooltipMessage()}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  renderSidebarButton(item, isExpanded, itemIsActive, itemCount, false)
                )}

                {/* Sub-items - only show if parent is expanded and not disabled */}
                {item.expandable && item.subItems && isExpanded && !shouldBeDisabled && (
                  <div className="ml-4 mt-1 flex flex-col gap-1">
                    {item.subItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const subIsActive = isSubItemActive(subItem.id);
                      const subCount = getCount(subItem.countKey);
                      const subIsHidden = isRouteDisabled(subItem.id);
                      const subIsGated = isItemDisabled(subItem.id);
                      const subIsDisabled = subIsHidden || subIsGated;
                      
                      // Skip hidden sub-items
                      if (subIsHidden) {
                        return null;
                      }
                      
                      const subItemButton = (
                        <button
                          key={subItem.id}
                          onClick={() => !subIsDisabled && handleMobileItemClick(subItem.id)}
                          disabled={subIsDisabled}
                          className={cn(
                            'w-full h-10 flex items-center justify-between px-3 rounded-[10px] text-base transition-colors text-left',
                            subIsDisabled
                              ? 'text-gray-400 cursor-not-allowed'
                              : subIsActive 
                                ? 'bg-brand-100 text-brand-800'
                                : 'text-gray-700 hover:bg-surface-secondary'
                          )}
                        >
                          <div className="flex items-center gap-2">
                            {SubIcon && <SubIcon className={cn('w-4 h-4', subIsDisabled && 'opacity-50')} />}
                            <span className="tracking-tight">{subItem.label}</span>
                            {subItem.artifactType && hasArtifactDocument(subItem.artifactType) && !subIsDisabled && (
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" title="Has document" />
                            )}
                          </div>
                          {subCount !== undefined && subCount > 0 && !subIsDisabled && (
                            <span className="px-2 py-0.5 bg-surface-tertiary text-gray-600 text-xs rounded-full min-w-[23px] text-center">
                              {subCount}
                            </span>
                          )}
                        </button>
                      );
                      
                      if (subIsGated) {
                        return (
                          <Tooltip key={subItem.id}>
                            <TooltipTrigger asChild>
                              <span className="block w-full">
                                {subItemButton}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[280px]">
                              <p>{getBlockedTooltip(subItem.id)}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }
                      
                      return subItemButton;
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        
        {/* Add Template Button - Fixed to bottom */}
        <div className="shrink-0 p-4 border-t border-surface-border">
          <button 
            onClick={onAddTemplate}
            className="w-full h-11 flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-[10px] text-gray-500 hover:border-brand-300 hover:text-gray-700 transition-colors"
          >
            <PlusCircle className="w-5 h-5" />
            <span className="tracking-tight">Add Template</span>
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
